import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { DeleteOutlined } from "@ant-design/icons";
import type { RootState } from "../../../redux/appState";
import {
  saveAssumptions,
  updateAssumptionsDraft,
} from "../../../redux/coilConfigurator/actions";
import {
  buildRecommendationOptionsInput,
  type RecommendationOptionsFormValues,
} from "../../../web-app";
import {
  addCalibrationPointToForm,
  assumptionsToFormValues,
  formValuesEqual,
  MODEL_COEFFICIENT_FIELDS,
  removeCalibrationPointFromForm,
} from "../domain/assumptionsForm";
import InfoAffordance from "../components/InfoAffordance";
import CcButton from "../components/CcButton";
import "./assumptions-panel.css";

interface AssumptionsPanelProps {
  draftAssumptions: RootState["coilConfigurator"]["assumptions"]["active"];
  savedAssumptions: RootState["coilConfigurator"]["assumptions"]["saved"];
  onDraftChange: (assumptions: ReturnType<typeof buildRecommendationOptionsInput>) => void;
  onSave: () => void;
}

const AssumptionsRemoveButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <CcButton variant="icon" className="assumptions-remove-btn" onClick={onClick} aria-label={label} danger>
    <DeleteOutlined />
  </CcButton>
);

const UnitField = ({
  label,
  glossaryKey,
  unit,
  value,
  step = "0.1",
  onChange,
}: {
  label: string;
  glossaryKey?: string;
  unit: string;
  value: string;
  step?: string;
  onChange: (value: string) => void;
}) => (
  <label>
    <span className="assumptions-field-label">
      {label}
      {glossaryKey ? <InfoAffordance glossaryKey={glossaryKey} /> : null}
    </span>
    <div className="field-with-unit">
      <input
        type="number"
        className="pih-numeric-input"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="field-unit">{unit}</span>
    </div>
  </label>
);

const NumberField = ({
  label,
  glossaryKey,
  value,
  step = "0.1",
  onChange,
}: {
  label: string;
  glossaryKey?: string;
  value: string;
  step?: string;
  onChange: (value: string) => void;
}) => (
  <label>
    <span className="assumptions-field-label">
      {label}
      {glossaryKey ? <InfoAffordance glossaryKey={glossaryKey} /> : null}
    </span>
    <input
      type="number"
      className="pih-numeric-input"
      step={step}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const AssumptionsPanel = ({
  draftAssumptions,
  savedAssumptions,
  onDraftChange,
  onSave,
}: AssumptionsPanelProps) => {
  const draftFormValues = useMemo(
    () => (draftAssumptions ? assumptionsToFormValues(draftAssumptions) : null),
    [draftAssumptions],
  );

  const savedFormValues = useMemo(
    () => (savedAssumptions ? assumptionsToFormValues(savedAssumptions) : null),
    [savedAssumptions],
  );

  const [formValues, setFormValues] = useState<RecommendationOptionsFormValues | null>(
    draftFormValues,
  );
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackIsError, setFeedbackIsError] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const skipNextDraftSyncRef = useRef(false);

  useEffect(() => {
    if (skipNextDraftSyncRef.current) {
      skipNextDraftSyncRef.current = false;
      return;
    }
    setFormValues(draftFormValues);
  }, [draftFormValues]);

  useEffect(() => {
    if (!draftFormValues || !savedFormValues) return;

    const coefficientChanged = MODEL_COEFFICIENT_FIELDS.some(
      (field) => draftFormValues[field.key] !== savedFormValues[field.key],
    );
    if (coefficientChanged) {
      setAdvancedOpen(true);
    }
  }, [draftFormValues, savedFormValues]);

  const isDirty = formValues && savedFormValues ? !formValuesEqual(formValues, savedFormValues) : false;

  const applyFormValues = useCallback(
    (next: RecommendationOptionsFormValues) => {
      skipNextDraftSyncRef.current = true;
      setFormValues(next);
      try {
        const built = buildRecommendationOptionsInput(next);
        onDraftChange(built);
        setFeedback(null);
        setFeedbackIsError(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Invalid assumptions data";
        setFeedback(message);
        setFeedbackIsError(true);
      }
    },
    [onDraftChange],
  );

  const updateField = <K extends keyof RecommendationOptionsFormValues>(
    key: K,
    value: RecommendationOptionsFormValues[K],
  ) => {
    if (!formValues) return;
    applyFormValues({ ...formValues, [key]: value });
  };

  const updateCoefficient = (key: (typeof MODEL_COEFFICIENT_FIELDS)[number]["key"], value: string) => {
    if (!formValues) return;
    applyFormValues({ ...formValues, [key]: value });
  };

  const updateCalibrationPoint = (
    index: number,
    patch: Partial<RecommendationOptionsFormValues["frequencyCalibrationPoints"][number]>,
  ) => {
    if (!formValues) return;
    applyFormValues({
      ...formValues,
      frequencyCalibrationPoints: formValues.frequencyCalibrationPoints.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    });
  };

  const handleSave = () => {
    if (!formValues) return;
    try {
      const built = buildRecommendationOptionsInput(formValues);
      onDraftChange(built);
      onSave();
      setFeedback("Assumptions saved.");
      setFeedbackIsError(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save assumptions";
      setFeedback(message);
      setFeedbackIsError(true);
    }
  };

  if (!formValues) {
    return (
      <section className="assumptions-workspace panel-card">
        <p className="placeholder-text">Loading assumptions…</p>
      </section>
    );
  }

  return (
    <section className="assumptions-workspace panel-card">
      <form
        id="recommendation-options-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <div className="panel-heading assumptions-panel__heading">
          <div className="panel-heading__left">
            <h2>Recommendation Assumptions</h2>
          </div>
          <div className="button-row">
            <span className={`dirty-state${isDirty ? " dirty-state--unsaved" : ""}`}>
              {isDirty ? "Unsaved changes" : "Saved"}
            </span>
            <CcButton variant="primary" onClick={handleSave} disabled={!isDirty}>
              Save Assumptions
            </CcButton>
          </div>
        </div>

        {feedback && (
          <div
            className={feedbackIsError ? "options-feedback options-feedback-error" : "options-feedback"}
            role="alert"
          >
            {feedback}
          </div>
        )}

        <div className="assumptions-card">
          <div className="assumptions-section">
            <h3 className="assumptions-section__title">Named assumptions</h3>
            <div className="input-grid assumptions-field-grid">
              <UnitField
                label="Reference Frequency"
                glossaryKey="referenceFrequency"
                unit="Hz"
                value={formValues.referenceFrequencyHz}
                onChange={(value) => updateField("referenceFrequencyHz", value)}
              />
              <UnitField
                label="Preferred Turn Spacing"
                glossaryKey="preferredTurnSpacing"
                unit="mm"
                value={formValues.preferredTurnSpacingMm}
                onChange={(value) => updateField("preferredTurnSpacingMm", value)}
              />
              <NumberField
                label="Minimum Turns"
                glossaryKey="minimumTurns"
                step="1"
                value={formValues.minimumTurns}
                onChange={(value) => updateField("minimumTurns", value)}
              />
              <NumberField
                label="Maximum Turns"
                glossaryKey="maximumTurns"
                step="1"
                value={formValues.maximumTurns}
                onChange={(value) => updateField("maximumTurns", value)}
              />
              <UnitField
                label="Coil Clearance"
                glossaryKey="coilClearance"
                unit="mm"
                value={formValues.coilClearanceMm}
                onChange={(value) => updateField("coilClearanceMm", value)}
              />
              <UnitField
                label="Required Overhang"
                glossaryKey="requiredOverhang"
                unit="mm"
                value={formValues.requiredOverhangMm}
                onChange={(value) => updateField("requiredOverhangMm", value)}
              />
              <UnitField
                label="Tie Bar Diameter"
                glossaryKey="tieBarDiameter"
                unit="mm"
                value={formValues.tieBarDiameterMm}
                onChange={(value) => updateField("tieBarDiameterMm", value)}
              />
            </div>
          </div>

          <div className="assumptions-section">
            <h3 className="assumptions-section__title">Advisory thresholds</h3>
            <div className="input-grid assumptions-field-grid">
              <UnitField
                label="Low Utilization Threshold"
                glossaryKey="lowUtilizationThreshold"
                unit="%"
                step="0.01"
                value={formValues.lowPowerUtilization}
                onChange={(value) => updateField("lowPowerUtilization", value)}
              />
              <UnitField
                label="Coverage Margin"
                glossaryKey="coverageMargin"
                unit="mm"
                value={formValues.minimumCoilCoverageMarginMm}
                onChange={(value) => updateField("minimumCoilCoverageMarginMm", value)}
              />
              <UnitField
                label="Fit Margin"
                glossaryKey="fitMargin"
                unit="mm"
                value={formValues.minimumCoilFrameSizeMarginMm}
                onChange={(value) => updateField("minimumCoilFrameSizeMarginMm", value)}
              />
              <UnitField
                label="Power Boundary Margin"
                glossaryKey="powerBoundaryMargin"
                unit="%"
                step="0.01"
                value={formValues.powerUtilizationBoundaryMargin}
                onChange={(value) => updateField("powerUtilizationBoundaryMargin", value)}
              />
            </div>
          </div>

          <div className="assumptions-section">
            <div className="assumptions-section-header">
              <h3 className="assumptions-section__title">Frequency Calibration</h3>
              <CcButton variant="primary" onClick={() => applyFormValues(addCalibrationPointToForm(formValues))}>
                + Add point
              </CcButton>
            </div>
            <div className="table-scroll">
              <table className="calibration-table" aria-label="Frequency calibration curve">
                <thead>
                  <tr>
                    <th scope="col">Frequency, Hz</th>
                    <th scope="col">Heating Factor</th>
                    <th scope="col">Cooldown Factor</th>
                    <th scope="col">
                      <span className="sr-only">Delete</span>
                    </th>
                  </tr>
                </thead>
                <tbody id="frequency-calibration-points">
                  {formValues.frequencyCalibrationPoints.map((point, index) => (
                    <tr key={`calibration-${index}-${point.frequencyHz}`}>
                      <td>
                        <input
                          type="number"
                          className="pih-numeric-input"
                          step="0.1"
                          value={point.frequencyHz}
                          onChange={(event) =>
                            updateCalibrationPoint(index, { frequencyHz: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="pih-numeric-input"
                          step="0.001"
                          value={point.heatingCycleFactor}
                          onChange={(event) =>
                            updateCalibrationPoint(index, { heatingCycleFactor: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="pih-numeric-input"
                          step="0.001"
                          value={point.cooldownFactor}
                          onChange={(event) =>
                            updateCalibrationPoint(index, { cooldownFactor: event.target.value })
                          }
                        />
                      </td>
                      <td>
                        <AssumptionsRemoveButton
                          label="Delete calibration point"
                          onClick={() =>
                            applyFormValues(removeCalibrationPointFromForm(formValues, index))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <details
            className="advanced-calibration"
            open={advancedOpen || undefined}
            onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
          >
            <summary>Advanced Calibration</summary>
            <fieldset>
              <legend>Raw empirical coefficients</legend>
              <div className="input-grid details-grid">
                {MODEL_COEFFICIENT_FIELDS.map((field) => (
                  <label key={field.key}>
                    {field.label}
                    <input
                      type="number"
                      className="pih-numeric-input"
                      step="0.01"
                      name={field.key}
                      value={formValues[field.key]}
                      onChange={(event) => updateCoefficient(field.key, event.target.value)}
                    />
                  </label>
                ))}
              </div>
            </fieldset>
          </details>
        </div>
      </form>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  draftAssumptions: state.coilConfigurator.assumptions.active,
  savedAssumptions: state.coilConfigurator.assumptions.saved,
});

const mapDispatchToProps = {
  onDraftChange: updateAssumptionsDraft,
  onSave: saveAssumptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssumptionsPanel);
